import React, { useEffect, useState, useCallback } from 'react';
import { logsAPI } from '../../services/api';
import LoadingSpinner from '../../components/Common/LoadingSpinner';

const ACTION_LABELS = {
  'auth.login':       { label:'Login',                color:'bg-blue-100 text-blue-700'    },
  'auth.logout':      { label:'Logout',               color:'bg-slate-100 text-slate-600'  },
  'employee.create':  { label:'Employee Created',     color:'bg-green-100 text-green-700'  },
  'employee.update':  { label:'Employee Updated',     color:'bg-yellow-100 text-yellow-700'},
  'employee.delete':  { label:'Employee Deleted',     color:'bg-red-100 text-red-700'      },
  'document.upload':  { label:'Document Uploaded',    color:'bg-indigo-100 text-indigo-700'},
  'document.delete':  { label:'Document Deleted',     color:'bg-orange-100 text-orange-700'},
  'visa.transition':  { label:'Visa Transition',      color:'bg-violet-100 text-violet-700'},
  'visa.stem_apply':  { label:'STEM Applied',         color:'bg-purple-100 text-purple-700'},
  'visa.stem_approve':{ label:'STEM Approved',        color:'bg-emerald-100 text-emerald-700'},
};

const ROLE_BADGE = {
  super_admin: 'bg-red-100 text-red-700',
  manager:     'bg-amber-100 text-amber-700',
  viewer:      'bg-slate-100 text-slate-600',
};

function fmtDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('en-US', {
    month:'short', day:'numeric', year:'numeric',
    hour:'numeric', minute:'2-digit', hour12:true,
  });
}

export default function ActivityLogs() {
  const [logs,     setLogs]     = useState([]);
  const [admins,   setAdmins]   = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  const [search,   setSearch]   = useState('');
  const [action,   setAction]   = useState('');
  const [adminId,  setAdminId]  = useState('');

  const load = useCallback(() => {
    setLoading(true);
    logsAPI.list({ page, per_page: 50, search: search || undefined, action: action || undefined, admin_id: adminId || undefined })
      .then((r) => {
        const d = r.data.data;
        setLogs(d.logs);
        setTotal(d.total);
        setLastPage(d.last_page);
        setAdmins(d.admins);
      })
      .catch(() => setError('Failed to load activity logs.'))
      .finally(() => setLoading(false));
  }, [page, search, action, adminId]);

  useEffect(() => { load(); }, [load]);

  function applyFilter(e) {
    e.preventDefault();
    setPage(1);
    load();
  }

  const uniqueActions = Object.keys(ACTION_LABELS);

  return (
    <div className="space-y-4">

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <form onSubmit={applyFilter} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-40">
            <label className="block text-xs font-medium text-slate-500 mb-1">Search</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, details…"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Action</label>
            <select value={action} onChange={(e) => setAction(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">All actions</option>
              {uniqueActions.map((a) => (
                <option key={a} value={a}>{ACTION_LABELS[a]?.label || a}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Admin</label>
            <select value={adminId} onChange={(e) => setAdminId(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">All admins</option>
              {admins.map((a) => (
                <option key={a.admin_id} value={a.admin_id}>{a.admin_username} ({a.admin_role})</option>
              ))}
            </select>
          </div>
          <button type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
            Filter
          </button>
          <button type="button" onClick={() => { setSearch(''); setAction(''); setAdminId(''); setPage(1); }}
            className="text-slate-500 hover:text-slate-700 text-sm px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">
            Clear
          </button>
        </form>
      </div>

      {/* Summary */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{total} log {total === 1 ? 'entry' : 'entries'}</p>
        <div className="flex gap-2">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
            className="px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-50">
            ← Prev
          </button>
          <span className="px-3 py-1.5 text-xs text-slate-500">Page {page} / {lastPage}</span>
          <button onClick={() => setPage((p) => Math.min(lastPage, p + 1))} disabled={page >= lastPage}
            className="px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-50">
            Next →
          </button>
        </div>
      </div>

      {/* Log table */}
      {loading ? (
        <LoadingSpinner fullPage={false} />
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6 text-center">{error}</div>
      ) : logs.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">No activity logs found.</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Time</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Admin</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Action</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Target</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Details</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logs.map((log) => {
                const meta = ACTION_LABELS[log.action] || { label: log.action, color:'bg-slate-100 text-slate-600' };
                return (
                  <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(log.created_at)}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800">{log.admin_username}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${ROLE_BADGE[log.admin_role] || ROLE_BADGE.viewer}`}>
                        {log.admin_role.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${meta.color}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {log.entity_name && <p className="font-medium">{log.entity_name}</p>}
                      {log.entity_type && <p className="text-xs text-slate-400 capitalize">{log.entity_type} {log.entity_id ? `#${log.entity_id}` : ''}</p>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 max-w-48">
                      {log.details && typeof log.details === 'object'
                        ? Object.entries(log.details).map(([k, v]) => v ? (
                            <span key={k} className="inline-block bg-slate-100 rounded px-1.5 py-0.5 mr-1 mb-0.5">
                              {k}: <b>{String(v)}</b>
                            </span>
                          ) : null)
                        : log.details}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400 font-mono">{log.ip_address || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
